import { useState } from "react";
import type { Workshop } from "@/content/workshops";
import { briefByWorkshop, type BriefItem } from "@/features/workshop/content";
import {
  BRIEF_ACTION_MODALS,
  BRIEF_ICON_NAMES,
  type BriefActionModal,
  type BriefRowOverride,
} from "../workshop-override-content";
import {
  clearEffectiveOverrideField,
  setEffectiveOverride,
  useEffectiveWorkshopOverride,
} from "@/stores/useEffectiveWorkshopOverride";
import { AddButton, Field, ListCard, SectionFrame, Select, TextInput } from "../editor-kit";
import { genId, moveItem, sameJson } from "../editor-utils";

/**
 * Editor row. For DEFAULT rows `icon` and the action are inherited (the
 * fields are inert constants); the power user relabels / sets meta / hides /
 * reorders. For AUTHORED rows (added here) the icon + action ARE editable —
 * the row opens a modal or an external URL.
 */
type Row = {
  id: string;
  label: string;
  meta: string;
  hidden: boolean;
  isNew: boolean;
  icon: string;
  action: "external" | BriefActionModal;
  url: string;
};

function buildRows(
  override: ReturnType<typeof useEffectiveWorkshopOverride>,
  defaults: BriefItem[],
): Row[] {
  const ovById = new Map((override?.brief ?? []).map((r) => [r.id, r]));
  const defIds = new Set(defaults.map((d) => d.id));
  const rows: Row[] = defaults.map((d) => {
    const o = ovById.get(d.id);
    return {
      id: d.id,
      label: o?.label ?? d.label,
      meta: o?.meta ?? d.meta ?? "",
      hidden: o?.hidden ?? false,
      isNew: false,
      icon: "link",
      action: "external",
      url: "",
    };
  });
  for (const o of override?.brief ?? []) {
    if (defIds.has(o.id)) continue;
    rows.push({
      id: o.id,
      label: o.label ?? "",
      meta: o.meta ?? "",
      hidden: o.hidden ?? false,
      isNew: true,
      icon: o.icon ?? "link",
      action: o.modal ?? "external",
      url: o.url ?? "",
    });
  }
  const order = override?.briefOrder;
  if (order && order.length > 0) {
    const pos = new Map(order.map((id, i) => [id, i] as const));
    return [...rows].sort(
      (a, b) =>
        (pos.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
        (pos.get(b.id) ?? Number.MAX_SAFE_INTEGER),
    );
  }
  return rows;
}

/** Brief panel item-row editor — relabel / meta / show-hide / reorder the
 *  default rows, and add authored rows that open a modal or an external link. */
export function BriefEditor({ wsId }: { wsId: Workshop["id"] }) {
  const override = useEffectiveWorkshopOverride(wsId);
  const defaults = briefByWorkshop[wsId] ?? [];
  const current = buildRows(override, defaults);
  const [draft, setDraft] = useState<Row[]>(current);

  const setRow = (i: number, patch: Partial<Row>) =>
    setDraft((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const save = () => {
    const defById = new Map(defaults.map((d) => [d.id, d]));
    const brief: BriefRowOverride[] = [];
    const persistedIds: string[] = [];
    for (const r of draft) {
      if (r.isNew) {
        if (!r.label.trim()) continue; // don't persist an empty authored row
        const row: BriefRowOverride = { id: r.id, label: r.label, icon: r.icon };
        if (r.meta) row.meta = r.meta;
        if (r.action === "external") {
          if (r.url) row.url = r.url;
        } else {
          row.modal = r.action;
        }
        brief.push(row);
        persistedIds.push(r.id);
        continue;
      }
      persistedIds.push(r.id);
      const d = defById.get(r.id);
      if (!d) continue;
      const diff: BriefRowOverride = { id: r.id };
      let changed = false;
      if (r.label !== d.label) {
        diff.label = r.label;
        changed = true;
      }
      if (r.meta !== (d.meta ?? "")) {
        diff.meta = r.meta;
        changed = true;
      }
      if (r.hidden) {
        diff.hidden = true;
        changed = true;
      }
      if (changed) brief.push(diff);
    }
    const orderChanged = !sameJson(persistedIds, defaults.map((d) => d.id));
    setEffectiveOverride(wsId, {
      brief: brief.length > 0 ? brief : undefined,
      briefOrder: orderChanged ? persistedIds : undefined,
    });
  };

  return (
    <SectionFrame
      description="The launcher rows in the Brief panel. Default rows keep their icon + what they open; added rows open a modal or an external link."
      overridden={override?.brief !== undefined || override?.briefOrder !== undefined}
      dirty={!sameJson(draft, current)}
      onSave={save}
      onReset={() => {
        clearEffectiveOverrideField(wsId, "brief");
        clearEffectiveOverrideField(wsId, "briefOrder");
        setDraft(buildRows(undefined, defaults));
      }}
    >
      {draft.map((r, i) => (
        <ListCard
          key={r.id}
          title={r.isNew ? "Added row" : undefined}
          onMoveUp={i > 0 ? () => setDraft(moveItem(draft, i, -1)) : undefined}
          onMoveDown={i < draft.length - 1 ? () => setDraft(moveItem(draft, i, 1)) : undefined}
          onRemove={r.isNew ? () => setDraft(draft.filter((_, j) => j !== i)) : undefined}
        >
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_140px]">
            <Field label="Label">
              <TextInput value={r.label} onChange={(e) => setRow(i, { label: e.target.value })} />
            </Field>
            <Field label="Meta (right text)">
              <TextInput value={r.meta} onChange={(e) => setRow(i, { meta: e.target.value })} />
            </Field>
          </div>

          {r.isNew ? (
            <>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Field label="Icon">
                  <Select
                    value={r.icon}
                    onChange={(v) => setRow(i, { icon: v })}
                    options={BRIEF_ICON_NAMES.map((n) => ({ value: n, label: n }))}
                  />
                </Field>
                <Field label="Opens">
                  <Select
                    value={r.action}
                    onChange={(v) =>
                      setRow(i, {
                        action: v as Row["action"],
                        // url only applies to the external action; clear it on
                        // switch so a stale value can't linger (and skew dirty).
                        url: v === "external" ? r.url : "",
                      })
                    }
                    options={[
                      { value: "external", label: "External link" },
                      ...BRIEF_ACTION_MODALS.map((m) => ({ value: m, label: `Open: ${m}` })),
                    ]}
                  />
                </Field>
              </div>
              {r.action === "external" && (
                <Field label="Link URL" hint="Opens in a new tab.">
                  <TextInput
                    value={r.url}
                    placeholder="https://..."
                    onChange={(e) => setRow(i, { url: e.target.value })}
                  />
                </Field>
              )}
            </>
          ) : (
            <label className="flex items-center gap-2 font-mono text-fs-label tracking-[0.12em] text-[color:var(--text-secondary)] uppercase">
              <input
                type="checkbox"
                checked={!r.hidden}
                onChange={(e) => setRow(i, { hidden: !e.target.checked })}
              />
              Shown in Brief panel
            </label>
          )}
        </ListCard>
      ))}
      <AddButton
        label="Add row"
        onClick={() =>
          setDraft([
            ...draft,
            {
              id: genId("brief"),
              label: "",
              meta: "",
              hidden: false,
              isNew: true,
              icon: "link",
              action: "external",
              url: "",
            },
          ])
        }
      />
    </SectionFrame>
  );
}
